import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import CardsNewIcon from 'ext-ui/svg/icon/CardsNew'
import React from 'react'
import { useSelector } from 'react-redux'
import SvgMerchantPending from 'src/components/Svgs/SvgMerchantPending'
import Tag from 'src/components/Tag/Tag'
import TextAvatar from 'src/components/TextAvatar/TextAvatar'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes } from 'src/enums'
import MerchantIcon from 'src/feature/MerchantIcon/MerchantIcon'
import { dateFormatString } from 'src/localization/Dte'
import { getLocaleInfo } from 'src/localization/locales'
import { useTranslate } from 'src/localization/Trns'
import {
  ConnectedPlatformStatus,
  TransactionDetailModel,
} from 'src/models/transaction.models'
import { UserListModel } from 'src/models/user.models'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectIsBetaOrgNormalizedMerchant } from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'

import style, { iconStyle } from './TransactionSummarySection.style'

interface TransactionSummarySectionProps {
  transaction: TransactionDetailModel
  cardUser: UserListModel
}

const TransactionSummarySection: React.FC<TransactionSummarySectionProps> = ({
  transaction,
  cardUser,
}): React.ReactElement => {
  const translate = useTranslate()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const enableNormalizedMerchantFlag = useSelector(
    selectFeatureFlaggerFromState,
  )?.enable_normalized_merchant
  const isWhitelistedNormalizedMerchant = useSelector(
    selectIsBetaOrgNormalizedMerchant,
  )
  const enableNormalizedMerchant =
    enableNormalizedMerchantFlag || isWhitelistedNormalizedMerchant
  const merchantImageSrc = transaction.merchantImageSrc('medium')
  const { data: user } = useQueryOwnUser({})
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const syncedToERP = transaction.connectedPlatforms?.find(
    (c) =>
      c.platform === activeIntegration?.connectedPlatform &&
      c.syncStatus === ConnectedPlatformStatus.Success,
  )

  return (
    <div css={style} className={'transactionSummarySection'}>
      <div className={'flex-column gap-4'}>
        <div className={'flex-row gap-2 align-baseline font-h1 text-primary'}>
          {transaction.clearingOrAuthLabel(true)}
          {transaction.currency !== getLocaleInfo(locale).currency && (
            <div className={'font-regular text-secondary'}>
              {transaction.currency}
            </div>
          )}
        </div>
        <div className={'details'}>
          <span className={'flex-row gap-1 align-center'}>
            <CardsNewIcon />
            {transaction.vcnDisplayName}
            <Tag fontSize={10} text={`${transaction.vcnLast4}`} />
          </span>
          {transaction.clearedAt && (
            <span>{dateFormatString(transaction.clearedAt, 'l')}</span>
          )}
          <span>{transaction.pseudoStatusLabelNew}</span>
          {user?.hasOrgBookkeeperPermissions && syncedToERP && (
            <TooltipIcon
              icon={
                <img css={iconStyle} src={activeIntegration?.logo} alt={''} />
              }
            >
              {translate('Synced to ${orgLvlIntegration}', {
                orgLvlIntegration: activeIntegration?.platformName || '',
              })}
            </TooltipIcon>
          )}
        </div>
      </div>
      <div className={'timeline'}>
        <div className={'timeline-event'}>
          {enableNormalizedMerchant ? (
            <MerchantIcon
              size={36}
              merchantImgSrc={merchantImageSrc}
              mccGroupKey={transaction.mccGroupKey ?? 'OTHER'}
            />
          ) : (
            <TextAvatar
              size={AvatarSizes.MediumLarge}
              text={transaction.merchantName?.[0]}
              defaultIcon={<SvgMerchantPending />}
            />
          )}
          <div className={'flex-column'}>
            <div className={'font-h3 text-primary'}>
              {transaction.normalizedMerchantNameOrUnknown}
              {transaction.normalizedMerchant && (
                <TooltipIcon>
                  {translate('Original Merchant: ${merchantName}', {
                    merchantName:
                      transaction.merchantName ?? translate('Unknown Merchant'),
                  })}
                </TooltipIcon>
              )}
            </div>
            <div className={'font-md text-secondary'}>
              {transaction.mccGroup}
            </div>
          </div>
        </div>
        <div className={'timeline-event'}>
          <UserAvatar user={cardUser} size={AvatarSizes.MediumLarge} />
          <div className={'flex-column'}>
            <div className={'font-h3 text-primary'}>
              {transaction.recipientName}
            </div>
            {transaction.date && (
              <div className={'font-md text-secondary'}>
                {dateFormatString(transaction.date, 'l, LT')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TransactionSummarySection
